/**
 * Drawers: menu, search and localization sheets (<kvs-drawer>), plus the mobile menu
 * accordion and the country filter. The cart drawer lives in cart-drawer.js and joins
 * the same "one overlay at a time" rule through the kvs:overlay-open event.
 *
 *   <button data-kvs-drawer-open="KvsSearchDrawer">   opens a drawer
 *   <button data-kvs-drawer-close>                     closes the drawer it sits in
 */

if (!customElements.get('kvs-drawer')) {
  class KvsDrawer extends HTMLElement {
    connectedCallback() {
      // Drawers are rendered inside the header section, which sits in its own stacking context
      // below other fixed UI (free shipping bar, apps). Move them to <body> once, replacing a
      // stale copy left behind when the theme editor re-renders the section.
      if (this.parentElement !== document.body) {
        Array.from(document.body.children).forEach((node) => {
          if (node !== this && node.id === this.id) node.remove();
        });
        document.body.appendChild(this);
        return;
      }

      if (this.ready) return;
      this.ready = true;

      this.panel = this.querySelector('.kvs-drawer__panel');
      this.addEventListener('click', (event) => {
        const handle = event.target.closest('[data-kvs-menu-drag-handle]');
        if (handle && event.detail > 0 && performance.now() < (this.suppressHandleClickUntil || 0)) {
          event.preventDefault();
          event.stopPropagation();
          return;
        }
        if (handle || event.target.closest('[data-kvs-drawer-close]')) this.close();
      });
      this.dragHandle = this.id === 'KvsMenuDrawer' ? this.querySelector('[data-kvs-menu-drag-handle]') : null;
      if (this.dragHandle) {
        this.phoneMenu = window.matchMedia('(max-width: 749px)');
        this.onMenuBreakpoint = () => this.resetMenuDrag();
        this.phoneMenu.addEventListener('change', this.onMenuBreakpoint);
        this.dragHandle.addEventListener('pointerdown', event => this.beginMenuDrag(event));
        this.dragHandle.addEventListener('pointermove', event => this.moveMenuDrag(event));
        this.dragHandle.addEventListener('pointerup', event => this.finishMenuDrag(event));
        this.dragHandle.addEventListener('pointercancel', event => this.finishMenuDrag(event, true));
        this.dragHandle.addEventListener('lostpointercapture', event => this.finishMenuDrag(event, true));
      }
      this.addEventListener('keydown', (event) => {
        if (event.key === 'Tab' && this.isOpen) {
          const controls = getFocusableElements(this.panel).filter((element) =>
            element.tabIndex >= 0 && !element.closest('[hidden], [inert]') &&
            element.getClientRects().length && getComputedStyle(element).visibility !== 'hidden'
          );
          const first = controls[0] || this.panel;
          const last = controls[controls.length - 1] || this.panel;
          if (!controls.length || (event.shiftKey && document.activeElement === first) ||
              (!event.shiftKey && document.activeElement === last) || document.activeElement === this.panel) {
            event.preventDefault();
            (event.shiftKey ? last : first).focus();
          }
          event.stopPropagation();
          return;
        }
        if (event.key !== 'Escape') return;
        event.stopPropagation();
        this.close();
      });

      this.onOtherOverlay = (event) => {
        if (event.detail?.source !== this && this.isOpen) this.close(false);
      };
      document.addEventListener('kvs:overlay-open', this.onOtherOverlay);

      if (typeof trapFocus === 'function' && typeof removeTrapFocus === 'function') {
        document.querySelectorAll('[data-kvs-drawer-open]').forEach((trigger) => {
          if (trigger.dataset.kvsDrawerOpen !== this.id) return;
          trigger.setAttribute('aria-haspopup', 'dialog');
          trigger.setAttribute('aria-controls', this.id);
          trigger.setAttribute('aria-expanded', 'false');
        });
      }

      const closeAbove = Number(this.dataset.closeAbove);
      if (closeAbove) {
        this.mql = window.matchMedia(`(min-width: ${closeAbove}px)`);
        this.mql.addEventListener('change', (event) => event.matches && this.close(false));
      }
    }

    disconnectedCallback() {
      document.removeEventListener('kvs:overlay-open', this.onOtherOverlay);
      cancelAnimationFrame(this.focusFrame);
      this.phoneMenu?.removeEventListener('change', this.onMenuBreakpoint);
      this.resetMenuDrag();
    }

    beginMenuDrag(event) {
      if (!this.isOpen || !this.phoneMenu.matches || event.isPrimary === false || (event.pointerType === 'mouse' && event.button !== 0)) return;
      this.resetMenuDrag();
      this.menuDrag = { pointerId: event.pointerId, startY: event.clientY, startX: event.clientX, distance: 0, moved: false };
      this.classList.add('is-dragging');
      try { this.dragHandle.setPointerCapture(event.pointerId); } catch (_) { /* A cancelled pointer cannot be captured. */ }
    }

    moveMenuDrag(event) {
      const drag = this.menuDrag;
      if (!drag || drag.pointerId !== event.pointerId) return;
      const vertical = event.clientY - drag.startY;
      const horizontal = Math.abs(event.clientX - drag.startX);
      drag.distance = horizontal > Math.max(12, vertical) ? 0 : Math.max(0, vertical);
      drag.moved = drag.moved || Math.abs(vertical) > 6 || horizontal > 6;
      if (event.cancelable) event.preventDefault();
      this.panel.style.setProperty('--kvs-menu-drag-offset', `${drag.distance}px`);
    }

    finishMenuDrag(event, cancelled = false) {
      const drag = this.menuDrag;
      if (!drag || drag.pointerId !== event.pointerId) return;
      const close = !cancelled && drag.distance >= 90;
      if (drag.moved) this.suppressHandleClickUntil = performance.now() + 500;
      this.resetMenuDrag();
      if (close) this.close();
    }

    resetMenuDrag() {
      const pointerId = this.menuDrag?.pointerId;
      this.menuDrag = null;
      this.classList.remove('is-dragging');
      this.panel?.style.removeProperty('--kvs-menu-drag-offset');
      if (pointerId !== undefined && this.dragHandle?.hasPointerCapture(pointerId)) this.dragHandle.releasePointerCapture(pointerId);
    }

    get isOpen() {
      return this.classList.contains('is-open');
    }

    open(trigger) {
      if (this.isOpen) return;
      this.resetMenuDrag();
      this.opener = trigger || document.activeElement;
      // A selector inside the mobile menu closes that menu. Return focus to its
      // visible opener instead of a button inside the now-hidden menu.
      const parentDrawer = this.opener?.closest?.('kvs-drawer.is-open');
      this.returnFocus = parentDrawer && parentDrawer !== this
        ? parentDrawer.returnFocus || parentDrawer.opener
        : this.opener;

      document.dispatchEvent(new CustomEvent('kvs:overlay-open', { detail: { source: this } }));

      this.classList.add('is-open');
      this.setAttribute('aria-hidden', 'false');
      document.body.classList.add('overflow-hidden');
      this.opener?.setAttribute?.('aria-expanded', 'true');

      const focusTarget = this.querySelector('[data-kvs-autofocus]') || this.panel;
      trapFocus(this.panel, focusTarget);
      // A visibility transition can reject synchronous focus, including the
      // short reduced-motion transition. Retry after the opening style paints.
      if (!this.contains(document.activeElement)) {
        this.focusFrame = requestAnimationFrame(() => {
          this.focusFrame = requestAnimationFrame(() => {
            this.focusFrame = 0;
            if (this.isConnected && this.isOpen && !this.contains(document.activeElement)) {
              trapFocus(this.panel, focusTarget);
            }
          });
        });
      }
      this.dispatchEvent(new CustomEvent('kvs-drawer:open', { bubbles: true }));
    }

    close(restoreFocus = true) {
      this.resetMenuDrag();
      if (!this.isOpen) return;
      cancelAnimationFrame(this.focusFrame);

      this.classList.remove('is-open');
      this.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('overflow-hidden');
      this.opener?.setAttribute?.('aria-expanded', 'false');
      removeTrapFocus(restoreFocus ? this.returnFocus : null);
      this.dispatchEvent(new CustomEvent('kvs-drawer:close', { bubbles: true }));
    }
  }

  customElements.define('kvs-drawer', KvsDrawer);
}

document.addEventListener('click', (event) => {
  const opener = event.target.closest('[data-kvs-drawer-open]');
  if (opener) {
    const drawer = document.getElementById(opener.dataset.kvsDrawerOpen);
    if (drawer?.ready && typeof drawer.open === 'function' && typeof trapFocus === 'function' && typeof removeTrapFocus === 'function') {
      event.preventDefault();
      drawer.open(opener);
    }
    return;
  }

  const toggle = event.target.closest('[data-kvs-accordion-toggle]');
  if (toggle) {
    const panel = document.getElementById(toggle.getAttribute('aria-controls'));
    if (!panel) return;
    const open = toggle.getAttribute('aria-expanded') !== 'true';
    toggle.setAttribute('aria-expanded', String(open));
    panel.classList.toggle('is-open', open);
    panel.firstElementChild.toggleAttribute('inert', !open);
  }
});

document.addEventListener('input', (event) => {
  const filter = event.target.closest('[data-kvs-filter]');
  if (!filter) return;

  const list = document.getElementById(filter.dataset.kvsFilter);
  if (!list) return;

  // Compared without accents, so "nemecko" finds "Německo".
  const plain = (text) => text.normalize('NFD').replace(/\p{M}/gu, '').toLocaleLowerCase();
  const term = plain(filter.value.trim());
  let shown = 0;
  list.querySelectorAll('li').forEach((item) => {
    item.hidden = term !== '' && !plain(item.textContent).includes(term);
    if (!item.hidden) shown += 1;
  });

  // A sentence instead of an empty sheet, and a spoken result count (strings from the theme: the empty-state
  // paragraph in the markup and window.accessibilityStrings.countrySelectorSearchCount).
  const empty = document.querySelector(`[data-kvs-filter-empty="${list.id}"]`);
  if (empty) empty.hidden = shown > 0;
  const status = document.querySelector(`[data-kvs-filter-status="${list.id}"]`);
  if (status) {
    const template = window.accessibilityStrings && window.accessibilityStrings.countrySelectorSearchCount;
    if (shown === 0) status.textContent = empty ? empty.textContent.trim() : '';
    else status.textContent = term !== '' && template ? template.replace('[count]', shown) : '';
  }
});
